/**
 * Channel Partner (CP): application, CP profile and the CP Hub.
 *
 * A CP is an extra capability of an existing customer or provider account —
 * the same JWT authenticates both, and the backend scopes every call to the
 * signed-in account. Contract: "Channel Partner: Frontend Integration Guide"
 * (28 Sep 2026); backend module `app/api/v1/channel_partner.py`.
 *
 * Every CP endpoint returns an untyped dict (no `response_model`), so the
 * OpenAPI spec has no response schemas to generate from. The types below are
 * hand-written from `app/services/channel_partner_service.py`
 * (`application_view` / `profile_view` / `context`) and
 * `app/schemas/channel_partner.py` — see BACKEND_QUIRKS.md.
 *
 * Endpoints:
 *   GET   /api/v1/channel-partners/me                          → account + application + profile + capability flags
 *   PATCH /api/v1/channel-partners/me                          → edit the approved CP profile (no re-review)
 *   GET   /api/v1/channel-partners/options                     → partner types, states, published agreement
 *   POST  /api/v1/channel-partners/applications                → submit the application (201)
 *   GET   /api/v1/channel-partners/customers                   → attributed customers (Hub)
 *   GET   /api/v1/channel-partners/services                    → attributed services + overall counts (Hub)
 *   POST  /api/v1/channel-partners/services/{id}/notifications → nudge the customer (202)
 *   GET   /api/v1/channel-partners/documents/reusable          → confirmed PAN/GST files already on the account
 *   POST  /api/v1/channel-partners/documents/uploads           → presigned PUT for a new PAN/GST file (201)
 *   POST  /api/v1/channel-partners/documents/{id}/confirm      → confirm the PUT landed
 *   GET   /api/v1/channel-partners/documents/{id}/download     → presigned GET (own file or published agreement)
 *
 * `can_apply` / `can_access_hub` from `/me` are the ONLY source of truth for
 * what to show — never infer Hub access from the mere presence of `profile`.
 */
import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ApiError, confirmUploadWithRetry, createKeyFactory } from '@finmin/api-client';
import { apiClient } from '@/lib/api';
import { queryClient } from './query-client';

// ---------- types -----------------------------------------------------------

export type ChannelPartnerAccountRole = 'customer' | 'provider';
export type ChannelPartnerApplicationStatus = 'draft' | 'pending' | 'approved' | 'rejected';
/** Admin-configured display group of a service. `null` = unmapped state. */
export type ChannelPartnerHubStatus = 'pending' | 'ongoing' | 'completed' | 'cancelled';
export type ChannelPartnerDocumentType = 'pan' | 'gst';
export type ChannelPartnerNotificationPreset = 'document_pending' | 'action_required';

export interface ChannelPartnerType {
  id: string;
  label: string;
}

export interface ChannelPartnerAgreement {
  id: string;
  title: string;
  document_id: string;
  published_at: string;
}

export interface ChannelPartnerOptions {
  partner_types: ChannelPartnerType[];
  /** Submit a state exactly as returned. */
  states: string[];
  /** `null` until an admin publishes one — no application can be submitted before that. */
  agreement: ChannelPartnerAgreement | null;
}

/** Fields shared by the application record and the saved CP profile. */
export interface ChannelPartnerProfileFields {
  cp_email: string | null;
  partner_type: string | null;
  other_partner_type: string | null;
  organisation_name: string | null;
  state: string | null;
  city: string | null;
  acquisition_source: string | null;
  goals: string | null;
  pan: string | null;
  /** Derived server-side from a supplied PAN. */
  pan_type: string | null;
  gstin: string | null;
  pan_document_id: string | null;
  gst_document_id: string | null;
}

export interface ChannelPartnerApplication extends ChannelPartnerProfileFields {
  id: string;
  status: ChannelPartnerApplicationStatus;
  full_name: string | null;
  mobile_e164: string | null;
  /** Set on an admin invitation draft. */
  invited_mobile: string | null;
  customer_id: string | null;
  provider_id: string | null;
  /** The agreement version accepted on submission. */
  agreement_id: string | null;
  accepted_at: string | null;
  submitted_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  /** Rejected applications only: 30 days after the decision. */
  reapply_allowed_at: string | null;
}

export interface ChannelPartnerProfile extends ChannelPartnerProfileFields {
  id: string;
  /** The code to share with new customers (separate from the ordinary referral code). */
  cp_code: string | null;
  approved_at: string | null;
  customer_id: string | null;
  provider_id: string | null;
}

export interface ChannelPartnerContext {
  account: {
    name: string | null;
    mobile: string;
    email: string | null;
    role: ChannelPartnerAccountRole;
  };
  /** Latest own submission, an invitation draft, or null. */
  application: ChannelPartnerApplication | null;
  profile: ChannelPartnerProfile | null;
  can_apply: boolean;
  can_access_hub: boolean;
  reapply_allowed_at: string | null;
}

/** Body for `POST /applications`. Optional fields are omitted, never sent empty. */
export interface ChannelPartnerApplicationPayload {
  cp_email: string;
  partner_type: string;
  other_partner_type?: string;
  organisation_name?: string;
  state: string;
  city: string;
  acquisition_source?: string;
  goals?: string;
  pan?: string;
  gstin?: string;
  pan_document_id?: string;
  gst_document_id?: string;
  agreement_id: string;
  agreement_accepted: true;
}

/** Body for `PATCH /me` — any subset; `null` clears an optional field. Extra keys are rejected. */
export type ChannelPartnerProfileUpdate = Partial<
  Record<Exclude<keyof ChannelPartnerProfileFields, 'pan_type'>, string | null>
>;

export interface ChannelPartnerPage<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface ChannelPartnerCustomer {
  id: string;
  name: string | null;
  /** Masked by the backend, e.g. "+91***3210". */
  mobile: string | null;
  email: string | null;
  onboarded_at: string;
}

/** Decimals arrive as strings inside items and as numbers in `counts`. */
export type DecimalValue = string | number;

export interface ChannelPartnerService {
  id: string;
  customer_id: string;
  customer_name: string | null;
  service_type: string;
  service_name: string;
  /** Actual service-request state (CREATED, IN_PROGRESS, ON_HOLD, COMPLETED, CANCELLED). */
  status: string;
  hub_status: ChannelPartnerHubStatus | null;
  created_at: string;
  /** Not a completion date. */
  updated_at: string | null;
  customer_tat_min: DecimalValue | null;
  customer_tat_max: DecimalValue | null;
  expected_tat: string | null;
  /** `null` = no recorded saving; `0` = a recorded zero. */
  money_saved: DecimalValue | null;
  rating: number | null;
  feedback: string | null;
}

/** Overall attributed activity — unaffected by list filters. */
export interface ChannelPartnerCounts {
  customers_onboarded: number;
  total_service_requests: number;
  pending_services: number;
  money_saved: DecimalValue;
  converted_customers: number;
}

export interface ChannelPartnerServicesPage extends ChannelPartnerPage<ChannelPartnerService> {
  counts: ChannelPartnerCounts;
}

export interface ChannelPartnerCustomerFilters {
  page?: number;
  page_size?: number;
  /** Customer-name substring. */
  name?: string;
}

export interface ChannelPartnerServiceFilters extends ChannelPartnerCustomerFilters {
  customer_id?: string;
  service_type?: string;
  hub_status?: ChannelPartnerHubStatus;
}

export type ChannelPartnerNotificationPayload =
  | { preset: ChannelPartnerNotificationPreset }
  | { message: string };

export interface ChannelPartnerReusableDocument {
  document_id: string;
  /** `pan` / `gst` (account documents) or `cp_pan` / `cp_gst` (earlier CP uploads). */
  document_type: string;
  document_label: string | null;
  created_at: string;
}

export interface ChannelPartnerDocumentDownload {
  download_url: string;
  expires_in: number;
  content_type: string | null;
}

// ---------- query keys ------------------------------------------------------

export const channelPartnerKeys = createKeyFactory('channel-partners', {
  me: () => ['me'] as const,
  options: () => ['options'] as const,
  customers: (filters: ChannelPartnerCustomerFilters = {}) => ['customers', filters] as const,
  services: (filters: ChannelPartnerServiceFilters = {}) => ['services', filters] as const,
  reusableDocuments: () => ['reusable-documents'] as const,
});

// ---------- raw API functions -----------------------------------------------

const BASE = '/api/v1/channel-partners';

/** Drop empty filter values so the server defaults apply. */
function clean<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ) as Partial<T>;
}

/**
 * A 403 from a Hub list means this account lost (or never had) approved Hub
 * access — refresh `/me` so the Hub guard re-routes from the fresh flags.
 */
function refreshAccessOn403(err: unknown): never {
  if (err instanceof ApiError && err.status === 403) {
    void queryClient.invalidateQueries({ queryKey: channelPartnerKeys.me() });
  }
  throw err;
}

/** GET /api/v1/channel-partners/me */
export async function getChannelPartnerContext(signal?: AbortSignal) {
  const { data } = await apiClient.get<ChannelPartnerContext>(`${BASE}/me`, { signal });
  return data;
}

/** GET /api/v1/channel-partners/options */
export async function getChannelPartnerOptions(signal?: AbortSignal) {
  const { data } = await apiClient.get<ChannelPartnerOptions>(`${BASE}/options`, { signal });
  return data;
}

/** POST /api/v1/channel-partners/applications → 201 application record */
export async function submitChannelPartnerApplication(payload: ChannelPartnerApplicationPayload) {
  const { data } = await apiClient.post<ChannelPartnerApplication>(`${BASE}/applications`, payload);
  return data;
}

/** PATCH /api/v1/channel-partners/me → updated profile (applies immediately). */
export async function updateChannelPartnerProfile(changes: ChannelPartnerProfileUpdate) {
  const { data } = await apiClient.patch<ChannelPartnerProfile>(`${BASE}/me`, changes);
  return data;
}

/** GET /api/v1/channel-partners/customers */
export async function listChannelPartnerCustomers(
  filters: ChannelPartnerCustomerFilters = {},
  signal?: AbortSignal,
) {
  try {
    const { data } = await apiClient.get<ChannelPartnerPage<ChannelPartnerCustomer>>(
      `${BASE}/customers`,
      { params: clean(filters), signal },
    );
    return data;
  } catch (err) {
    return refreshAccessOn403(err);
  }
}

/** GET /api/v1/channel-partners/services */
export async function listChannelPartnerServices(
  filters: ChannelPartnerServiceFilters = {},
  signal?: AbortSignal,
) {
  try {
    const { data } = await apiClient.get<ChannelPartnerServicesPage>(`${BASE}/services`, {
      params: clean(filters),
      signal,
    });
    return data;
  } catch (err) {
    return refreshAccessOn403(err);
  }
}

/** POST /api/v1/channel-partners/services/{id}/notifications → 202 (accepted, not delivered). */
export async function sendChannelPartnerNotification(
  serviceRequestId: string,
  payload: ChannelPartnerNotificationPayload,
) {
  try {
    const { data } = await apiClient.post<{ status: 'accepted' }>(
      `${BASE}/services/${serviceRequestId}/notifications`,
      payload,
    );
    return data;
  } catch (err) {
    return refreshAccessOn403(err);
  }
}

/** GET /api/v1/channel-partners/documents/reusable */
export async function listReusableChannelPartnerDocuments(signal?: AbortSignal) {
  const { data } = await apiClient.get<ChannelPartnerReusableDocument[]>(
    `${BASE}/documents/reusable`,
    { signal },
  );
  return data;
}

/** GET /api/v1/channel-partners/documents/{id}/download → presigned GET URL (JSON, not bytes). */
export async function getChannelPartnerDocumentDownload(documentId: string) {
  const { data } = await apiClient.get<ChannelPartnerDocumentDownload>(
    `${BASE}/documents/${documentId}/download`,
  );
  return data;
}

/** Content types the backend accepts for a CP attachment (see app/services/document_storage.py). */
export const CHANNEL_PARTNER_UPLOAD_ACCEPT = '.pdf,.jpg,.jpeg,.png';

/**
 * Upload a new optional PAN/GST attachment: initiate → presigned PUT →
 * confirm. Only a confirmed id may be attached to an application or profile.
 * Confirm failures arrive as `ApiError` with a user-facing message
 * (410 missing upload, 415 bad signature, 503 storage unavailable).
 */
export async function uploadChannelPartnerDocument(
  file: File,
  documentType: ChannelPartnerDocumentType,
  onProgress?: (percent: number) => void,
): Promise<string> {
  const contentType = file.type || 'application/octet-stream';
  const { data: init } = await apiClient.post<{
    document_id: string;
    upload_url: string;
    expires_in: number;
  }>(`${BASE}/documents/uploads`, {
    filename: file.name,
    content_type: contentType,
    file_size: file.size,
    document_type: documentType,
  });

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', init.upload_url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new ApiError(`Upload failed (${xhr.status})`, xhr.status));
    xhr.onerror = () => reject(new ApiError('Network error during upload', 0));
    xhr.send(file);
  });

  const confirmed = await confirmUploadWithRetry(async () => {
    const { data } = await apiClient.post<{ document_id: string; upload_status: string }>(
      `${BASE}/documents/${init.document_id}/confirm`,
    );
    return data;
  });
  void queryClient.invalidateQueries({ queryKey: channelPartnerKeys.reusableDocuments() });
  return confirmed.document_id;
}

// ---------- hooks -----------------------------------------------------------

export function useChannelPartnerContext(
  options?: Omit<UseQueryOptions<ChannelPartnerContext, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ChannelPartnerContext, ApiError>({
    queryKey: channelPartnerKeys.me(),
    queryFn: ({ signal }) => getChannelPartnerContext(signal),
    ...options,
  });
}

export function useChannelPartnerOptions(
  options?: Omit<UseQueryOptions<ChannelPartnerOptions, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ChannelPartnerOptions, ApiError>({
    queryKey: channelPartnerKeys.options(),
    queryFn: ({ signal }) => getChannelPartnerOptions(signal),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useChannelPartnerCustomers(
  filters: ChannelPartnerCustomerFilters = {},
  options?: Omit<
    UseQueryOptions<ChannelPartnerPage<ChannelPartnerCustomer>, ApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<ChannelPartnerPage<ChannelPartnerCustomer>, ApiError>({
    queryKey: channelPartnerKeys.customers(filters),
    queryFn: ({ signal }) => listChannelPartnerCustomers(filters, signal),
    placeholderData: (prev) => prev,
    ...options,
  });
}

export function useChannelPartnerServices(
  filters: ChannelPartnerServiceFilters = {},
  options?: Omit<UseQueryOptions<ChannelPartnerServicesPage, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ChannelPartnerServicesPage, ApiError>({
    queryKey: channelPartnerKeys.services(filters),
    queryFn: ({ signal }) => listChannelPartnerServices(filters, signal),
    placeholderData: (prev) => prev,
    ...options,
  });
}

export function useReusableChannelPartnerDocuments(
  options?: Omit<
    UseQueryOptions<ChannelPartnerReusableDocument[], ApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<ChannelPartnerReusableDocument[], ApiError>({
    queryKey: channelPartnerKeys.reusableDocuments(),
    queryFn: ({ signal }) => listReusableChannelPartnerDocuments(signal),
    ...options,
  });
}

/** Submit, then refresh `/me` so the page re-renders from the new status. */
export function useSubmitChannelPartnerApplication(
  options?: UseMutationOptions<
    ChannelPartnerApplication,
    ApiError,
    ChannelPartnerApplicationPayload
  >,
) {
  return useMutation<ChannelPartnerApplication, ApiError, ChannelPartnerApplicationPayload>({
    mutationFn: submitChannelPartnerApplication,
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      void queryClient.invalidateQueries({ queryKey: channelPartnerKeys.me() });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
    onError: (err, vars, onMutateResult, context) => {
      // 409 = the application state moved on (already pending/approved, wait
      // period) and 422 may mean the agreement changed: refetch both sources.
      if (err.status === 409) {
        void queryClient.invalidateQueries({ queryKey: channelPartnerKeys.me() });
      }
      if (err.status === 422) {
        void queryClient.invalidateQueries({ queryKey: channelPartnerKeys.options() });
      }
      options?.onError?.(err, vars, onMutateResult, context);
    },
  });
}

export function useUpdateChannelPartnerProfile(
  options?: UseMutationOptions<ChannelPartnerProfile, ApiError, ChannelPartnerProfileUpdate>,
) {
  return useMutation<ChannelPartnerProfile, ApiError, ChannelPartnerProfileUpdate>({
    mutationFn: updateChannelPartnerProfile,
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      queryClient.setQueryData<ChannelPartnerContext>(channelPartnerKeys.me(), (prev) =>
        prev ? { ...prev, profile: data } : prev,
      );
      void queryClient.invalidateQueries({ queryKey: channelPartnerKeys.me() });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useSendChannelPartnerNotification(
  options?: UseMutationOptions<
    { status: 'accepted' },
    ApiError,
    { serviceRequestId: string; payload: ChannelPartnerNotificationPayload }
  >,
) {
  return useMutation<
    { status: 'accepted' },
    ApiError,
    { serviceRequestId: string; payload: ChannelPartnerNotificationPayload }
  >({
    mutationFn: ({ serviceRequestId, payload }) =>
      sendChannelPartnerNotification(serviceRequestId, payload),
    ...options,
  });
}
